import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useTheme } from '../ThemeContext/useTheme';
import type { IconMapType } from '../../icon-maps/base-theme';

interface IconContextProps {
  iconMap: IconMapType | null;
  requestIcons: () => void;
}

const IconContext = createContext<IconContextProps | undefined>(undefined);

export const IconProvider = ({ children }: { children: ReactNode }) => {
  const { theme } = useTheme();
  const [requested, setRequested] = useState(false);
  const requestIcons = useCallback(() => setRequested(true), []);
  const [iconMap, setIconMap] = useState<IconMapType | null>(null);

  useEffect(() => {
    if (!requested) return;
    let cancelled = false;
    const loadIconMap = async () => {
      try {
        let icons: IconMapType;

        switch (theme) {
          case 'dark': {
            const darkModule = await import('../../icon-maps/dark-theme');
            icons = darkModule.darkThemeIcons;
            break;
          }
          case 'indyweek': {
            const indyModule = await import('../../icon-maps/indyweek-theme');
            icons = indyModule.indyweekThemeIcons;
            break;
          }
          case 'assembly': {
            const assemblyModule = await import('../../icon-maps/assembly-theme');
            icons = assemblyModule.assemblyThemeIcons;
            break;
          }
          default: {
            // Use base theme for all others (Light Theme, buzzfeed, History)
            const baseModule = await import('../../icon-maps/base-theme');
            icons = baseModule.baseThemeIcons;
            break;
          }
        }

        if (!cancelled) setIconMap(icons);
      } catch (error) {
        console.warn('Failed to load icon map, falling back to base theme:', error);
        // Fallback to base theme
        try {
          const baseModule = await import('../../icon-maps/base-theme');
          if (!cancelled) setIconMap(baseModule.baseThemeIcons);
        } catch (fallbackError) {
          console.error('Failed to load base icon map:', fallbackError);
          if (!cancelled) setIconMap(null);
        }
      }
    };

    void loadIconMap();
    return () => {
      cancelled = true;
    };
  }, [theme, requested]);

  const contextValue = useMemo(() => ({ iconMap, requestIcons }), [iconMap, requestIcons]);

  return <IconContext.Provider value={contextValue}>{children}</IconContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useIcons = (): IconContextProps => {
  const context = useContext(IconContext);
  const requestIcons = context?.requestIcons;
  useEffect(() => {
    requestIcons?.();
  }, [requestIcons]);
  if (!context) {
    throw new Error('useIcons must be used within an IconProvider');
  }
  return context;
};
